// Presse-papier du titulaire — logique PURE : aucun DOM, aucun réseau.
//
// POURQUOI CE MODULE EXISTE. Sur un iPhone on ne voit jamais deux réponses à la
// fois. Le titulaire ASSEMBLE donc son dossier : il pose une question, garde la
// réponse, passe à la suivante, et sort une fiche à la fin — pour lui, son
// comptable, un confrère, un labo ou son avocat. Le presse-papier n'est pas un
// accessoire d'export : il remplace le tableau de bord qu'un écran de bureau
// afficherait d'un coup.
//
// Conséquence directe sur la conception : il n'y a QU'UN presse-papier et QU'UN
// générateur de fiche pour toute l'app. Chaque écran qui veut être « gardable »
// pousse ici une entrée normalisée, au lieu d'écrire son propre export — c'est ce
// qui évite N formats de sortie divergents pour N écrans.
//
// Le stockage est INJECTÉ (même contrat que session.js : getItem / setItem /
// removeItem). C'est ce qui rend le module testable sans navigateur, et ce qui
// permet de choisir le support côté appelant — le presse-papier doit survivre à
// la fermeture de l'app, donc localStorage, là où session.js vit en session.

// On réutilise le store en mémoire de session.js au lieu d'en écrire un second :
// c'est exactement le même contrat, et deux implémentations dériveraient.
import { memoryStore } from "./session.js";
export { memoryStore };

export const CLE_STOCKAGE = "bureau-presse-papier";

/**
 * Version du format persisté. Toute évolution incompatible l'incrémente : au
 * chargement, un format inconnu est jeté au lieu d'être deviné (cf. `charger`).
 */
export const VERSION_FORMAT = 1;

/**
 * Borne haute du presse-papier.
 *
 * Sans borne, le stockage gonfle jusqu'à l'échec d'écriture — et l'échec arrive
 * au pire moment : au moment de garder une réponse, pas au moment de la lire.
 * 50 est très au-delà d'un usage réel (une fiche de 50 lignes ne se lit plus),
 * donc la borne ne gêne personne, mais elle transforme une panne silencieuse en
 * message clair.
 */
export const MAX_ENTREES = 50;

/** Types reconnus. Un type inconnu ne fait pas échouer l'ajout : il retombe sur « note ». */
export const TYPES = ["prix", "marge", "echeance", "alerte", "fournisseur", "tresorerie", "note"];
const TYPE_DEFAUT = "note";

/**
 * Ordre des sections de la fiche — un ordre de LECTURE, pas l'ordre d'ajout.
 *
 * Ce qui appelle une décision passe devant (alertes, échéances, trésorerie),
 * les chiffres de référence ensuite (prix, marges), le contexte en dernier.
 * Un comptable ou un avocat lit le haut de la page ; y mettre une note libre
 * pendant qu'une condition dégradée attend en bas serait un contresens.
 */
const ORDRE_SECTIONS = ["alerte", "echeance", "tresorerie", "prix", "marge", "fournisseur", "note"];

const LIBELLES_SECTIONS = {
  alerte: "Alertes",
  echeance: "Échéances",
  tresorerie: "Trésorerie",
  prix: "Prix",
  marge: "Marges",
  fournisseur: "Fournisseurs",
  note: "Notes",
};

// ── État ─────────────────────────────────────────────────────────────────────

/** Presse-papier neuf. Toute fonction de ce module accepte aussi `null` (cf. `etatSain`). */
export function pressePapierVide() {
  return { v: VERSION_FORMAT, entrees: [] };
}

/**
 * Ramène n'importe quoi à un état exploitable.
 *
 * Aucune fonction publique ne doit lever parce que l'appelant a passé `undefined`
 * au premier rendu, avant que `charger` ait tourné. Un presse-papier illisible se
 * comporte comme un presse-papier vide — jamais comme une exception.
 */
function etatSain(etat) {
  if (!etat || typeof etat !== "object" || !Array.isArray(etat.entrees)) return pressePapierVide();
  return { v: VERSION_FORMAT, entrees: etat.entrees };
}

/**
 * Identifiant normalisé en chaîne.
 *
 * La coercition n'est pas cosmétique : l'id d'une facture arrive tantôt en nombre
 * (colonne SQL) tantôt en chaîne (attribut `data-id` du DOM). Sans ce passage par
 * String, `42` et `"42"` seraient deux entrées distinctes pour la MÊME réponse —
 * exactement le doublon silencieux que l'idempotence est censée empêcher.
 */
function normaliserId(v) {
  if (v == null) return "";
  return String(v).trim();
}

function texteOuNull(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
}

function normaliserEntree(element, id, titre, date) {
  return {
    id,
    // Un type inconnu est un défaut d'AFFICHAGE, pas d'intégrité : on ne perd pas
    // le geste du titulaire pour ça, l'entrée retombe dans « Notes ».
    type: TYPES.includes(element.type) ? element.type : TYPE_DEFAUT,
    titre,
    // `?? null` et non `|| null` : un prix de 0 € ou une valeur vide sont des
    // réponses légitimes, elles ne doivent pas être effacées par une garde laxiste.
    valeur: element.valeur ?? null,
    detail: texteOuNull(element.detail),
    source: texteOuNull(element.source),
    date,
  };
}

function refus(etat, motif, message) {
  // L'état est renvoyé INCHANGÉ : l'appelant peut réaffecter sans réfléchir, il
  // n'y a pas de chemin où un refus détruit ce qui était déjà gardé.
  return { ok: false, motif, message, etat };
}

// ── Opérations ───────────────────────────────────────────────────────────────

/**
 * Ajoute (ou met à jour) une entrée. Ne mute jamais `etat` : renvoie un nouvel état.
 *
 * IDEMPOTENT PAR `id` — c'est la propriété centrale du module. Sur un téléphone,
 * un bouton « garder » se tape deux fois : doigt qui glisse, retour arrière,
 * double-tap involontaire. Sans idempotence, la fiche part chez le comptable avec
 * la même ligne écrite deux fois, et personne ne s'en aperçoit avant lui.
 *
 * Retourne `{ ok, motif, message, etat }` plutôt que le seul état : l'appelant a
 * besoin de savoir s'il affiche « gardé », « mis à jour » ou un refus. `retirer`
 * et `vider`, eux, n'ont aucun cas d'échec à signaler et rendent l'état seul.
 */
export function ajouter(etat, element, maintenant = Date.now()) {
  const base = etatSain(etat);

  if (!element || typeof element !== "object") {
    return refus(base, "invalide", "Rien à garder.");
  }

  const id = normaliserId(element.id);
  if (!id) {
    // Sans id stable, l'idempotence est impossible : on ne peut PAS inventer un
    // identifiant ici, sinon chaque tap créerait une entrée neuve. Le refus est
    // le seul comportement honnête, et il se voit au développement.
    return refus(base, "invalide", "Cette réponse n'a pas d'identifiant : impossible de la garder sans risquer un doublon.");
  }

  const titre = typeof element.titre === "string" ? element.titre.trim() : "";
  if (!titre) {
    return refus(base, "invalide", "Cette réponse n'a pas de titre : elle serait illisible sur la fiche.");
  }

  const i = base.entrees.findIndex((e) => e.id === id);

  // La borne ne s'applique qu'aux entrées NEUVES : refuser la mise à jour d'une
  // entrée déjà gardée parce que le presse-papier est plein serait absurde — elle
  // n'ajoute rien au stockage, et le titulaire se retrouverait avec une valeur
  // périmée qu'il croit avoir rafraîchie.
  if (i === -1 && base.entrees.length >= MAX_ENTREES) {
    return refus(
      base,
      "plein",
      `Le presse-papier est plein (${MAX_ENTREES} éléments). Générez la fiche, puis videz-le.`,
    );
  }

  // Sur une mise à jour, la date d'origine est CONSERVÉE : elle dit quand le
  // titulaire a décidé de garder cette réponse, ce qui est l'ancre du raisonnement.
  // La valeur, elle, est bien remplacée par la plus fraîche.
  const date =
    i === -1
      ? texteOuNull(element.date) ?? new Date(maintenant).toISOString()
      : base.entrees[i].date;

  const entree = normaliserEntree(element, id, titre, date);
  const entrees = base.entrees.slice();
  if (i === -1) entrees.push(entree);
  else entrees[i] = entree; // remplacement EN PLACE : la position dans le raisonnement ne bouge pas

  return {
    ok: true,
    motif: i === -1 ? "ajout" : "mise-a-jour",
    message: null,
    etat: { v: VERSION_FORMAT, entrees },
  };
}

/** Retire une entrée. Retirer un id absent n'est pas une erreur : l'état revient tel quel. */
export function retirer(etat, id) {
  const base = etatSain(etat);
  const cible = normaliserId(id);
  return { v: VERSION_FORMAT, entrees: base.entrees.filter((e) => e.id !== cible) };
}

/**
 * Vide le presse-papier.
 * `etat` est accepté mais inutilisé : la signature reste homogène avec le reste
 * du module, et l'appelant n'a pas à se souvenir d'une exception.
 */
export function vider(_etat) {
  return pressePapierVide();
}

/** Cette réponse est-elle déjà gardée ? Sert au bouton à afficher « garder » ou « gardé ». */
export function contient(etat, id) {
  const cible = normaliserId(id);
  return etatSain(etat).entrees.some((e) => e.id === cible);
}

/**
 * Les entrées, dans l'ORDRE D'AJOUT — jamais trié.
 *
 * L'ordre dans lequel le titulaire a posé ses questions EST un ordre de
 * raisonnement : il a regardé la marge parce que le prix l'avait surpris. Retrier
 * par montant ou par date détruirait ce fil, et la fiche ne se relirait plus.
 * Le regroupement par type n'a lieu qu'à la génération de la fiche, où il sert la
 * lecture d'un tiers — et il préserve l'ordre d'ajout à l'intérieur de chaque section.
 *
 * Copie défensive : l'appelant ne doit pas pouvoir modifier l'état par la bande.
 */
export function lister(etat) {
  return etatSain(etat).entrees.slice();
}

/** Nombre d'entrées gardées (pastille du bouton). */
export function compter(etat) {
  return etatSain(etat).entrees.length;
}

// ── Persistance ──────────────────────────────────────────────────────────────

/**
 * Lit le presse-papier stocké. NE LÈVE JAMAIS.
 *
 * Cette fonction tourne au démarrage de l'app. Un presse-papier corrompu, écrit
 * par une version antérieure, ou tronqué par un onglet tué en cours d'écriture,
 * doit repartir vide — jamais empêcher l'app de s'ouvrir. C'est le même arbitrage
 * que `lireSession` : perdre un presse-papier est ennuyeux, ne plus démarrer est
 * bloquant. Le `try` englobe aussi `getItem` : Safari en navigation privée fait
 * lever le stockage lui-même, pas seulement `JSON.parse`.
 */
export function charger(store) {
  try {
    const brut = store.getItem(CLE_STOCKAGE);
    if (!brut) return pressePapierVide();

    const objet = JSON.parse(brut);
    if (!objet || typeof objet !== "object" || Array.isArray(objet)) return pressePapierVide();

    // Format d'une version antérieure : on le JETTE au lieu de le migrer à
    // l'aveugle. Une migration devinée produirait des entrées à moitié valides,
    // qui finiraient imprimées sur une fiche envoyée à un tiers.
    if (objet.v !== VERSION_FORMAT) return pressePapierVide();
    if (!Array.isArray(objet.entrees)) return pressePapierVide();

    const entrees = [];
    for (const brute of objet.entrees) {
      if (!brute || typeof brute !== "object") continue;
      const id = normaliserId(brute.id);
      const titre = typeof brute.titre === "string" ? brute.titre.trim() : "";
      if (!id || !titre) continue; // une entrée sans identité ni titre est irrécupérable
      if (entrees.some((e) => e.id === id)) continue; // dédoublonne un stockage déjà pollué
      entrees.push(normaliserEntree(brute, id, titre, texteOuNull(brute.date)));
      if (entrees.length >= MAX_ENTREES) break; // la borne vaut aussi à la relecture
    }
    return { v: VERSION_FORMAT, entrees };
  } catch {
    return pressePapierVide();
  }
}

/**
 * Écrit le presse-papier. Retourne `false` si le stockage a refusé.
 *
 * On ne laisse pas remonter l'exception : quota dépassé ou stockage bloqué, l'app
 * doit continuer de fonctionner pour la session en cours. Mais on le SIGNALE, car
 * un titulaire qui assemble une fiche pendant dix minutes doit savoir qu'elle ne
 * survivra pas à la fermeture de l'app.
 */
export function enregistrer(store, etat) {
  const base = etatSain(etat);
  try {
    store.setItem(CLE_STOCKAGE, JSON.stringify(base));
    return true;
  } catch {
    return false;
  }
}

/** Efface la trace persistée (le geste « vider » va souvent avec). */
export function effacer(store) {
  try {
    store.removeItem(CLE_STOCKAGE);
  } catch {
    /* stockage indisponible : rien à faire, l'état en mémoire fait foi */
  }
}

// ── Fiche ────────────────────────────────────────────────────────────────────

/**
 * Construit la fiche à rendre : `{ titre, sousTitre, sections, meta }`.
 *
 * Objet STRUCTURÉ, pas du HTML ni du texte : le même objet doit pouvoir être
 * rendu à l'écran, imprimé (fiche-impression.css), ou relu par un test. Générer
 * du balisage ici enfermerait la fiche dans un seul rendu et rendrait le module
 * impossible à tester sans DOM.
 *
 * `meta` porte le destinataire et l'officine — un même presse-papier sert à
 * fabriquer une fiche pour le comptable et une autre pour le labo, sans rien
 * regarder de plus.
 *
 * `maintenant` est injectable pour que l'horodatage soit testable ; sans ça le
 * test dépendrait de l'heure d'exécution.
 */
export function construireFiche(etat, meta = {}, maintenant = Date.now()) {
  const entrees = etatSain(etat).entrees;
  const m = meta && typeof meta === "object" ? meta : {};

  const sections = [];
  for (const type of ORDRE_SECTIONS) {
    const lot = entrees.filter((e) => e.type === type);
    // Une section vide n'est jamais créée : une fiche qui annonce « Marges » puis
    // ne montre rien laisse croire à une donnée manquante plutôt qu'à une donnée
    // non demandée. Sur une pièce transmise à un tiers, c'est une ambiguïté chère.
    if (lot.length === 0) continue;
    sections.push({ type, libelle: LIBELLES_SECTIONS[type], entrees: lot });
  }

  const quand = new Date(maintenant);
  const officine = texteOuNull(m.officine);
  const destinataire = texteOuNull(m.destinataire);

  return {
    titre: texteOuNull(m.titre) ?? "Fiche de synthèse",
    // Les morceaux absents sont retirés au lieu de laisser des séparateurs
    // orphelins (« · · 8 août ») en tête d'un document imprimé.
    sousTitre: [officine, destinataire ? `pour ${destinataire}` : null, dateFr(quand)]
      .filter(Boolean)
      .join(" · "),
    sections,
    meta: {
      officine,
      destinataire,
      // Les deux formes coexistent volontairement : l'ISO est la trace vérifiable
      // (et testable), la forme française est ce qui s'imprime en en-tête.
      genereLe: quand.toISOString(),
      genereLeFr: dateHeureFr(quand),
      nbEntrees: entrees.length,
      nbSections: sections.length,
    },
  };
}

function dateFr(d) {
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function dateHeureFr(d) {
  return `${dateFr(d)} à ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
}
